import * as React from 'react';

const SCROLL_THRESHOLD_PX = 152;

function supportsScrollTimeline(): boolean {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    return false;
  }

  try {
    return CSS.supports('animation-timeline', 'scroll()');
  } catch {
    return false;
  }
}

/**
 * Fallback для браузеров без поддержки `animation-timeline: scroll()`
 * (iOS Safari < 26, Chrome < 115, Samsung Internet < 23)
 *
 * Слушает window.scroll с rAF-троттлингом и переключает заданный класс
 * на DOM-узле, когда вертикальный скролл превышает порог.
 */
export function useScrollCollapseFallback(ref: React.RefObject<HTMLElement | null>, scrolledClassName: string): void {
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (supportsScrollTimeline()) {
      return;
    }

    const node = ref.current;
    if (!node || !scrolledClassName) {
      return;
    }

    let rafId = 0;
    let lastScrolled: boolean | null = null;

    const apply = (): void => {
      rafId = 0;
      const scrolled = window.scrollY > SCROLL_THRESHOLD_PX;
      if (scrolled === lastScrolled) {
        return;
      }
      lastScrolled = scrolled;
      node.classList.toggle(scrolledClassName, scrolled);
    };

    const onScroll = (): void => {
      if (rafId === 0) {
        rafId = window.requestAnimationFrame(apply);
      }
    };

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId !== 0) {
        window.cancelAnimationFrame(rafId);
      }
      node.classList.remove(scrolledClassName);
    };
  }, [ref, scrolledClassName]);
}
